import React from 'react';
import api from '../api/axios'; // Adjust the path based on your project structure
import { Modal, Form, Input, Button, Typography, Row, Col, Radio, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MailOutlined, UserOutlined, LockOutlined, GoogleOutlined, WindowsOutlined } from '@ant-design/icons';

const { Title } = Typography;

const SignUpModal = ({ visible, onClose }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const handleSignInRedirect = (e) => {
    e.preventDefault();
    onClose();
    navigate('/signin');
  };

  const handleSubmit = (values) => {
    const userData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      username: values.username,
      passwordHash: values.password,
      role: values.role || 'STUDENT', // Match backend case (uppercase)
    };

    api
      .post('/users/register', userData)
      .then((response) => {
        console.log('User registered successfully:', response.data);
        form.resetFields();

        Modal.success({
          title: 'Registration Successful',
          content: 'Your account has been created! Redirecting to sign-in...',
          onOk: () => {
            onClose();
            navigate('/signin');
          },
          okText: 'Continue to Sign In',
          afterClose: () => {
            onClose();
            navigate('/signin');
          },
          style: { borderRadius: '8px' },
          okButtonProps: {
            style: { background: '#13c2c2', borderColor: '#13c2c2', borderRadius: '4px' },
          },
        });
      })
      .catch((error) => {
        console.error('Error registering user:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers,
        });
        const errorMessage = error.response?.data?.message || error.message || 'Something went wrong!';
        Modal.error({
          title: 'Registration Failed',
          content: errorMessage,
          style: { borderRadius: '8px' },
          okButtonProps: {
            style: { background: '#fa8c16', borderColor: '#fa8c16', borderRadius: '4px' },
          },
        });
      });
  };

  const handleSocialSignUp = (provider) => {
    console.log(`Sign up with ${provider}`);
    Modal.info({
      title: 'Coming Soon',
      content: `Sign up with ${provider} is not yet implemented.`,
      style: { borderRadius: '8px' },
      okButtonProps: {
        style: { background: '#13c2c2', borderColor: '#13c2c2', borderRadius: '4px' },
      },
    });
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ minHeight: '500px', borderRadius: '12px', overflow: 'hidden' }} // Increased height for more fields
      destroyOnClose
      bodyStyle={{ padding: 0 }}
    >
      <Row style={{ minHeight: '500px' }}>
        <Col
          xs={0}
          sm={12}
          style={{
            backgroundImage: 'url(/images/jecthublogin.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(19, 194, 194, 0.5), rgba(250, 140, 22, 0.5))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              textAlign: 'center',
              padding: '20px',
            }}
          >
            <Title level={2} style={{ color: '#fff', fontWeight: '700', margin: 0 }}>
              Join JectHub Today
            </Title>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <Form
            form={form}
            style={{
              padding: '32px',
              height: '100%',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            validateMessages={{
              required: '${label} is required!',
              types: { email: 'Please enter a valid email!' },
            }}
            onFinish={handleSubmit}
          >
            <Title
              level={4}
              style={{
                background: 'linear-gradient(45deg, #13c2c2, #08979c)',
                color: '#fff',
                textAlign: 'center',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontWeight: '700',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
            >
              Sign Up
            </Title>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please enter your first name!' },
                    { min: 2, message: 'First name must be at least 2 characters!' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#13c2c2' }} />}
                    placeholder="First Name"
                    size="large"
                    style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please enter your last name!' },
                    { min: 2, message: 'Last name must be at least 2 characters!' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#13c2c2' }} />}
                    placeholder="Last Name"
                    size="large"
                    style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Enter your email address"
                size="large"
                style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              />
            </Form.Item>

            <Form.Item
              label="Username"
              name="username"
              rules={[
                { required: true, message: 'Please enter a username!' },
                { min: 3, message: 'Username must be at least 3 characters!' },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Choose a username"
                size="large"
                style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a password!' },
                { min: 6, message: 'Password must be at least 6 characters!' },
              ]}
              style={{ marginBottom: '24px' }} // Added space before button, matching SignInModal
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Create a password"
                size="large"
                style={{ borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              />
            </Form.Item>

            <Form.Item
              name="role"
              wrapperCol={{ span: 24 }}
              style={{ marginBottom: '24px', textAlign: 'center' }} // Centered roles with spacing
            >
              <Radio.Group
                defaultValue="STUDENT"
                style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}
              >
                <Tooltip title="Learn and grow with JectHub">
                  <Radio.Button
                    value="STUDENT"
                    style={{
                      borderRadius: '6px',
                      background: '#fff',
                      borderColor: '#13c2c2',
                      color: '#13c2c2',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#13c2c2';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#13c2c2';
                    }}
                  >
                    Student
                  </Radio.Button>
                </Tooltip>
                <Tooltip title="Guide and mentor others">
                  <Radio.Button
                    value="MENTOR"
                    style={{
                      borderRadius: '6px',
                      background: '#fff',
                      borderColor: '#13c2c2',
                      color: '#13c2c2',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#13c2c2';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#13c2c2';
                    }}
                  >
                    Mentor
                  </Radio.Button>
                </Tooltip>
                <Tooltip title="Partner and collaborate with JectHub">
                  <Radio.Button
                    value="VENDOR"
                    style={{
                      borderRadius: '6px',
                      background: '#fff',
                      borderColor: '#13c2c2',
                      color: '#13c2c2',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#13c2c2';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#13c2c2';
                    }}
                  >
                    Vendor
                  </Radio.Button>
                </Tooltip>
              </Radio.Group>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                style={{
                  background: 'linear-gradient(45deg, #fa8c16, #d46b08)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  height: '48px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #d46b08, #ad4e00)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #fa8c16, #d46b08)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                }}
              >
                Create Account
              </Button>
            </Form.Item>

            <div style={{ textAlign: 'center', margin: '20px 0', position: 'relative' }}>
              <div style={{ borderBottom: '1px solid #dcdcdc' }} />
              <span
                style={{
                  background: '#fff',
                  padding: '0 12px',
                  position: 'absolute',
                  top: '-10px',
                  color: '#888',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                OR
              </span>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Button
                  size="large"
                  block
                  style={{
                    background: 'linear-gradient(45deg, #4285F4, #357ABD)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    height: '44px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => handleSocialSignUp('Google')}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #357ABD, #2A62A0)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #4285F4, #357ABD)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                  }}
                >
                  <GoogleOutlined /> Google
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  size="large"
                  block
                  style={{
                    background: 'linear-gradient(45deg, #00A4EF, #0086C9)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    height: '44px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => handleSocialSignUp('Microsoft')}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #0086C9, #0068A3)';
                    e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(45deg, #00A4EF, #0086C9)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                  }}
                >
                  <WindowsOutlined /> Microsoft
                </Button>
              </Col>
            </Row>

            <Form.Item style={{ textAlign: 'center', marginTop: '20px' }}>
              <span style={{ color: '#595959', fontSize: '14px' }}>
                Already have an account?{' '}
                <a
                  href="#"
                  onClick={handleSignInRedirect}
                  style={{ color: '#13c2c2', fontWeight: 'bold', transition: 'color 0.3s ease' }}
                  onMouseEnter={(e) => (e.target.style.color = '#08979c')}
                  onMouseLeave={(e) => (e.target.style.color = '#13c2c2')}
                >
                  Sign In
                </a>
              </span>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      {/* Custom CSS */}
      <style jsx global>{`
        .ant-modal {
          animation: slideIn 0.3s ease-in-out;
        }
        .ant-form-item-label > label {
          color: #595959;
          font-weight: 500;
        }
        .ant-input-affix-wrapper, .ant-input-password {
          transition: all 0.3s ease;
        }
        .ant-input-affix-wrapper:hover, .ant-input-password:hover {
          border-color: #13c2c2 !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
        }
        .ant-input-affix-wrapper-focused, .ant-input-password-focused {
          border-color: #fa8c16 !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
        }
        .ant-radio-button-wrapper-checked {
          background: #13c2c2 !important;
          border-color: #13c2c2 !important;
          color: #fff !important;
        }
        .ant-radio-button-wrapper-checked:hover {
          background: #08979c !important;
          border-color: #08979c !important;
        }
        @keyframes slideIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Modal>
  );
};

export default SignUpModal;